import type { ReactNode } from 'react'
import { AlertCircle, Lock, RefreshCw, WifiOff, CloudOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { describeError, type DescribedError } from '@/lib/errors'
import { formatClockTime } from '@/lib/format'
import { useNetworkStatus } from '@/hooks/useOnline'
import { useRegisterOfflineGap } from '@/stores/loadGapStore'
import { ListSkeleton } from './Skeletons'

/**
 * The shared loading → error → forbidden → empty → content switch (UX-011,
 * UX-014, UX-096, UX-010, UX-018).
 *
 * The rule it enforces: an empty state or a zero is only ever shown after a
 * SUCCESSFUL load. A failed load shows what went wrong and a working
 * "Try again"; a 403 shows the access card (no Retry — it can't help);
 * offline with cached data shows the data plus an "Updated 10:42" marker.
 *
 * Works with all three fetch styles in the app:
 *   1. React Query results      (`useQuery(...)`)                → isLoading / error / data / refetch
 *   2. useApi wrapper hooks     (`useJobs()`, `useContacts()`…)   → loading / error / rawError / data / refetch
 *   3. legacy useApiData hooks  (`useWorkflows()`…)               → loading / error / rawError / data / refetch
 */

// ─── normalising the three hook shapes ──────────────────────────────────────

export interface QueryLike<T> {
  data?: T | null
  // React Query
  isLoading?: boolean
  isPending?: boolean
  isFetching?: boolean
  isError?: boolean
  dataUpdatedAt?: number | null
  // wrapper hooks
  loading?: boolean
  fetching?: boolean
  /** RQ: the Error. Wrapper hooks: the human message string. */
  error?: unknown
  /** Wrapper hooks: the original error, so 403/offline can be told apart. */
  rawError?: unknown
  refetch?: () => unknown
}

export interface NormalizedQuery<T> {
  status: 'loading' | 'error' | 'success'
  data: T | null
  error: unknown
  refetch?: () => unknown
  fetching: boolean
  updatedAt: number | null
}

export function normalizeQuery<T>(q: QueryLike<T>): NormalizedQuery<T> {
  const data = (q.data ?? null) as T | null
  const loading = q.isLoading ?? q.loading ?? false
  const error = q.rawError ?? q.error ?? null
  const hasError = q.isError ?? !!error
  const status = hasError ? 'error' : loading && data == null ? 'loading' : 'success'
  return {
    status,
    data,
    error,
    refetch: q.refetch,
    fetching: !!(q.isFetching ?? q.fetching),
    updatedAt: q.dataUpdatedAt || null,
  }
}

// ─── cards ──────────────────────────────────────────────────────────────────

interface ErrorStateProps {
  /** Any error (Axios, string, Error) — or a pre-built DescribedError. */
  error: unknown
  /** "load your invoices" → title "Couldn't load your invoices". */
  action?: string
  /** "see invoices" → "Your role can't see invoices. Ask an admin…" */
  forbiddenWhat?: string
  onRetry?: () => unknown
  /** Smaller inline variant for a widget inside a page. */
  compact?: boolean
  className?: string
}

function isDescribed(e: unknown): e is DescribedError {
  return !!e && typeof e === 'object' && 'kind' in e && 'retryable' in e && 'title' in e
}

/**
 * Error card. Forbidden → access card (no Retry). Offline → "You're offline".
 * Anything else → "Couldn't {action}" + human reason + Try again.
 */
export function ErrorState({ error, action, forbiddenWhat, onRetry, compact, className }: ErrorStateProps) {
  const d = isDescribed(error) ? error : describeError(error, { action, forbiddenWhat })
  // Tell the shell banner this screen is missing something because of the
  // network, so it doesn't claim "Showing what was loaded" (UX-018).
  useRegisterOfflineGap(d.kind === 'offline')
  if (d.kind === 'forbidden') {
    return <ForbiddenState title={d.title} message={d.message} compact={compact} className={className} />
  }
  const Icon = d.kind === 'offline' ? WifiOff : AlertCircle
  return (
    <div
      role="alert"
      data-testid="query-error"
      className={cn(
        compact ? 'ds-tile flex items-start gap-3 p-4 text-left' : 'ds-tile mx-auto max-w-md p-8 text-center',
        className,
      )}
    >
      <div className={cn('flex shrink-0 items-center justify-center rounded-full bg-white/[0.06]', compact ? 'h-9 w-9' : 'mx-auto mb-4 h-12 w-12')}>
        <Icon className={cn(compact ? 'h-4 w-4' : 'h-5 w-5')} style={{ color: 'var(--midnight-blush)' }} aria-hidden="true" />
      </div>
      <div className={cn(compact && 'min-w-0 flex-1')}>
        <h3 className={cn('font-semibold text-white', compact ? 'text-[15px]' : 'text-base')}>{d.title}</h3>
        <p className={cn('mt-1 text-sm', !compact && 'mx-auto max-w-sm')} style={{ color: 'var(--midnight-text2)' }}>
          {d.message}
        </p>
        {onRetry && d.retryable && (
          <button
            type="button"
            onClick={() => { void onRetry() }}
            className={cn('ds-btn ds-btn-lg', compact ? 'mt-3' : 'mt-5')}
          >
            <RefreshCw aria-hidden="true" />
            Try again
          </button>
        )}
      </div>
    </div>
  )
}

/** "You don't have access" card (the Reports pattern, shared). */
export function ForbiddenState({
  title = "You don't have access",
  message = "Your role can't open this. Ask an admin if you need access.",
  compact,
  className,
}: { title?: string; message?: string; compact?: boolean; className?: string }) {
  return (
    <div
      role="status"
      data-testid="query-forbidden"
      className={cn(compact ? 'ds-tile flex items-start gap-3 p-4' : 'ds-tile mx-auto max-w-md p-8 text-center', className)}
    >
      <div className={cn('flex shrink-0 items-center justify-center rounded-2xl bg-white/[0.06]', compact ? 'h-9 w-9' : 'mx-auto mb-4 h-14 w-14')}>
        <Lock className={cn(compact ? 'h-4 w-4' : 'h-6 w-6')} style={{ color: 'var(--midnight-text2)' }} aria-hidden="true" />
      </div>
      <div className={cn(compact && 'min-w-0 flex-1')}>
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <p className={cn('mt-2 text-sm', !compact && 'mx-auto max-w-sm')} style={{ color: 'var(--midnight-text2)' }}>{message}</p>
      </div>
    </div>
  )
}

/**
 * Marker for data that may be out of date: shown above content when the last
 * refresh failed or the device is offline. "Offline · Updated 10:42".
 */
export function StaleDataNotice({
  updatedAt,
  offline,
  error,
  onRetry,
  className,
}: { updatedAt?: number | null; offline?: boolean; error?: unknown; onRetry?: () => unknown; className?: string }) {
  const when = updatedAt ? ` · Updated ${formatClockTime(updatedAt)}` : ''
  const d = !offline && error ? describeError(error) : null
  return (
    <div
      role="status"
      data-testid="stale-notice"
      className={cn('mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3 py-2 text-[13px]', className)}
      style={{ background: 'var(--midnight-surface)', border: '0.5px solid var(--midnight-border-bright)', color: 'var(--midnight-text2)' }}
    >
      <CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {offline ? `Offline${when}. This may not be current.` : `Couldn't refresh${when}. ${d?.kind === 'server' ? 'Something went wrong on our side.' : 'Showing the last saved copy.'}`}
      </span>
      {onRetry && !offline && (
        <button type="button" onClick={() => { void onRetry() }} className="ds-btn ds-btn-sm">
          Try again
        </button>
      )}
    </div>
  )
}

// ─── the switch ─────────────────────────────────────────────────────────────

export interface QueryStateProps<T> {
  /** Any of the three hook shapes (see file header). */
  query: QueryLike<T>
  /** Rendered with the loaded data. */
  children: (data: T) => ReactNode
  /** Layout-matching placeholder. Default: a list skeleton. */
  skeleton?: ReactNode
  /** Return true when loaded data has nothing to show. */
  isEmpty?: (data: T) => boolean
  /** Shown only after a successful, empty load. */
  empty?: ReactNode
  /** Verb phrase for the error title: "load your invoices". */
  action?: string
  /** For the 403 card: "see invoices". */
  forbiddenWhat?: string
  /** Compact error/forbidden cards (for a widget inside a page). */
  compact?: boolean
  /** Hide the stale-data marker (e.g. the page already shows its own). */
  hideStaleNotice?: boolean
  /**
   * Replaces the error card when a failed load has nothing cached (not for a
   * 403). For a widget whose page already shows ONE error card with Try again,
   * so the widget only marks the gap instead of stacking another card.
   */
  errorFallback?: ReactNode
}

/**
 *   <QueryState
 *     query={useJobs({ status: 'SCHEDULED' })}
 *     action="load your jobs"
 *     forbiddenWhat="see jobs"
 *     skeleton={<ListSkeleton rows={5} />}
 *     isEmpty={(d) => d.items.length === 0}
 *     empty={<EmptyState title="No jobs yet" description="…" />}
 *   >
 *     {(d) => <JobList jobs={d.items} />}
 *   </QueryState>
 */
export function QueryState<T>({
  query,
  children,
  skeleton,
  isEmpty,
  empty,
  action = 'load this',
  forbiddenWhat,
  compact,
  hideStaleNotice,
  errorFallback,
}: QueryStateProps<T>) {
  const q = normalizeQuery(query)
  const { online, lastSuccess } = useNetworkStatus()

  if (q.status === 'loading') return <>{skeleton ?? <ListSkeleton />}</>

  if (q.status === 'error') {
    // Cached data + failed refresh: keep showing it, marked as stale — unless
    // the failure is a permission change, where the old data must go.
    const d = describeError(q.error, { action, forbiddenWhat })
    if (q.data != null && d.kind !== 'forbidden' && d.kind !== 'unauthorized') {
      const offline = d.kind === 'offline' || !online
      return (
        <>
          {/* Offline, the shell banner already says how old this is; a
              second "Offline · Updated" strip repeated it (UX-018). */}
          {!hideStaleNotice && !offline && (
            <StaleDataNotice
              offline={false}
              updatedAt={q.updatedAt ?? lastSuccess}
              error={q.error}
              onRetry={q.refetch}
            />
          )}
          {isEmpty?.(q.data) && empty ? empty : children(q.data)}
        </>
      )
    }
    if (errorFallback !== undefined && d.kind !== 'forbidden' && d.kind !== 'unauthorized') return <>{errorFallback}</>
    return <ErrorState error={d} onRetry={q.refetch} compact={compact} />
  }

  // No data and nothing in flight. Offline, React Query pauses the fetch
  // (no error, no data) — say so instead of shimmering forever. Otherwise
  // (e.g. a disabled query waiting on an id) keep the placeholder.
  if (q.data == null) {
    if (!online) return <ErrorState error={new TypeError('Failed to fetch')} action={action} compact={compact} />
    return <>{skeleton ?? <ListSkeleton />}</>
  }
  const data = q.data as T
  return (
    <>
      {isEmpty?.(data) && empty ? empty : children(data)}
    </>
  )
}
